/**
 * OPD, ETD and ETA are one chain, read from whichever end the surface knows:
 *
 *   OPD --[production duration]--> ETD --[shipping leg]--> ETA
 *
 * - NOOS knows the OPD (when the buyer orders) and wants ETD and ETA.
 * - Fashion knows the ETA (when goods must land) and wants ETD and OPD.
 * - A sent PO knows the supplier's ETD and wants an in-house ETA.
 *
 * Every date is exact day arithmetic on the lead times, with no week normalization, so
 * anchoring on any one of the three yields the same other two.
 */

const DAY_MS = 24 * 60 * 60 * 1000

// Millisecond arithmetic rather than date-fns `addDays`, which shifts in local time and drifts an
// hour across DST in the browser. Fractional lead times truncate, as `addDays` did.
function shiftDays(date: Date, days: number): Date {
  return new Date(date.getTime() + Math.trunc(days) * DAY_MS)
}

export type SupplyDateAnchor = { kind: "opd" | "etd" | "eta"; date: Date }

export type SupplyDates = { opd: Date; etd: Date; eta: Date }

/** A SKU's lead times, shaped like its settings row. Missing means 0 / not supplied. */
export type SkuLeadTimes = {
  /** The total OPD→ETA span (`L0`). */
  totalLeadTimeDays?: number | null
  /** Production only, when the source system sends it separately. */
  productionLeadTimeDays?: number | null
  /** Transport only, when the source system sends it separately. */
  transportLeadTimeDays?: number | null
}

export type SupplyLeadTimeInputs = {
  sku: SkuLeadTimes | undefined
  /** The vendor's per-warehouse shipping legs from Vendor Management. */
  vendorShippingLeadTimes: Record<string, number> | undefined
  warehouseId: string
}

/** OPD, ETD and ETA for one (vendor, sku, warehouse) order line, from whichever one is known. */
export function resolveSupplyDates({
  anchor,
  ...leadTimes
}: { anchor: SupplyDateAnchor } & SupplyLeadTimeInputs): SupplyDates {
  const shipping = resolveShippingLeadTimeDays(leadTimes)
  const production = computeProductionDurationDays(leadTimes)
  switch (anchor.kind) {
    case "opd": {
      const etd = shiftDays(anchor.date, production)
      return { opd: anchor.date, etd, eta: shiftDays(etd, shipping) }
    }
    case "etd":
      return {
        opd: shiftDays(anchor.date, -production),
        etd: anchor.date,
        eta: shiftDays(anchor.date, shipping),
      }
    case "eta": {
      const etd = shiftDays(anchor.date, -shipping)
      return { opd: shiftDays(etd, -production), etd, eta: anchor.date }
    }
  }
}

/**
 * Shipping leg for one (vendor, warehouse) pair.
 *
 * Vendor Management is authoritative — Carhartt's expected ETD is "ETA minus the shipping time set
 * on the vendor" (SUP-423). When that vendor has no leg configured for the warehouse we fall back
 * to the SKU's own ingested transport lead time rather than 0, so an ETD still accounts for
 * transport instead of collapsing onto the ETA.
 */
export function resolveShippingLeadTimeDays({
  sku,
  vendorShippingLeadTimes,
  warehouseId,
}: SupplyLeadTimeInputs): number {
  const configured = vendorShippingLeadTimes?.[warehouseId]
  if (configured != null && configured >= 0) return configured
  const transport = sku?.transportLeadTimeDays
  if (transport != null && transport >= 0) return transport
  return 0
}

/**
 * Production duration for a vendor's batch.
 *
 * Prefers the SKU's own production lead time when the source system sends one (SUP-423: Carhartt
 * reports it as `Minimum Lead Time in Days`, separate from transport). Otherwise infers it as
 * `max(0, L0 - L1)`: the SKU lead time (`L0`) includes the longest shipping leg (`L1`), so what is
 * left is the span the vendor spends producing — the same span every warehouse of that vendor
 * shares, which is why their ETDs align while their ETAs differ. `L1` is the vendor's longest
 * configured leg, else the SKU's transport time (which `L0` includes too), else 0 — so with
 * nothing configured the chain spans exactly `L0`.
 */
export function computeProductionDurationDays({
  sku,
  vendorShippingLeadTimes,
}: Omit<SupplyLeadTimeInputs, "warehouseId">): number {
  const production = sku?.productionLeadTimeDays
  if (production != null && production >= 0) return production
  const shippingLegs = Object.values(vendorShippingLeadTimes ?? {}).filter((v) => v >= 0)
  const longestShipping =
    shippingLegs.length > 0
      ? Math.max(...shippingLegs)
      : Math.max(0, sku?.transportLeadTimeDays ?? 0)
  return Math.max(0, (sku?.totalLeadTimeDays ?? 0) - longestShipping)
}
